import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { gql, useMutation, useQuery } from "@apollo/client";
import Header from "components/Header";
import { userClient } from "state/apolloClient";

const GET_MEMBER_FINES_GROUP = gql`
  query {
    memberFines {
      id
      userId
      groupId
      fineTypeId
      amountDue
      amountPaid
      dueDate
      statusId
      description
      createdBy
      createdDate
      updatedBy
      updatedDate
    }
  }
`;

const GET_FINE_TYPES = gql`
  query {
    fineTypes {
      id
      name
      defaultAmount
      paymentWindowDays
      isActive
    }
  }
`;

const GET_USERS = gql`
  query {
    users(user: {}) {
      id
      firstName
      lastName
      email
      isActive
    }
  }
`;

const CREATE_MEMBER_FINES_BULK = gql`
  mutation CreateMemberFinesBulk($memberFines: [MemberFinesInput!]!) {
    createMemberFinesBulk(memberFines: $memberFines)
  }
`;

const STATUS = {
  1: { label: "Due", color: "warning" },
  2: { label: "Partially Paid", color: "info" },
  3: { label: "Paid", color: "success" },
};

const currency = (n) =>
  n == null ? "-" : `KES ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const yyyyMmDd = (d = new Date()) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const buildInitialDraft = () => ({
  fineTypeId: "",
  amountDue: "",
  dueDate: yyyyMmDd(),
  description: "",
  memberIds: [],
});

const GroupFines = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const roleGroupId = Number(localStorage.getItem("roleGroupId"));
  const canManage = roleGroupId === 1 || roleGroupId === 2;

  const [tab, setTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const [draft, setDraft] = useState(buildInitialDraft());
  const [batchItems, setBatchItems] = useState([]);
  const [snack, setSnack] = useState({ type: "success", message: "" });

  const {
    data: finesData,
    loading: finesLoading,
    error: finesError,
    refetch: refetchFines,
  } = useQuery(GET_MEMBER_FINES_GROUP);

  const { data: fineTypesData, loading: fineTypesLoading } = useQuery(GET_FINE_TYPES);

  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS, {
    client: userClient,
    fetchPolicy: "cache-and-network",
  });

  const [createMemberFinesBulk, { loading: creatingBulk }] = useMutation(CREATE_MEMBER_FINES_BULK, {
    errorPolicy: "all",
  });

  const fineTypes = useMemo(
    () => (fineTypesData?.fineTypes || []).filter((ft) => ft.isActive),
    [fineTypesData]
  );

  const fineTypesMap = useMemo(() => {
    const map = new Map();
    fineTypes.forEach((ft) => map.set(Number(ft.id), ft));
    return map;
  }, [fineTypes]);

  const members = useMemo(() => {
    return (usersData?.users || [])
      .filter((u) => u?.isActive !== false)
      .map((u) => ({
        id: Number(u.id),
        label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || `Member #${u.id}`,
        email: u.email,
      }));
  }, [usersData]);

  const membersMap = useMemo(() => {
    const map = new Map();
    members.forEach((m) => map.set(Number(m.id), m));
    return map;
  }, [members]);

  const filteredRows = useMemo(() => {
    const rows = finesData?.memberFines ?? [];
    const base = !statusFilter ? rows : rows.filter((r) => String(r.statusId) === String(statusFilter));
    return base.map((r) => ({
      ...r,
      memberName: membersMap.get(Number(r.userId))?.label || `Member #${r.userId}`,
      fineTypeName: fineTypesMap.get(Number(r.fineTypeId))?.name || `Fine Type #${r.fineTypeId}`,
    }));
  }, [finesData, statusFilter, membersMap, fineTypesMap]);


  const selectedMembers = useMemo(
    () => members.filter((m) => draft.memberIds.includes(m.id)),
    [members, draft.memberIds]
  );

  const preparedFineCount = useMemo(
    () => batchItems.reduce((sum, batch) => sum + batch.memberIds.length, 0),
    [batchItems]
  );

  const handleFineTypeChange = (value) => {
    const nextFineType = fineTypes.find((f) => String(f.id) === String(value));
    const nextAmount = nextFineType?.defaultAmount != null ? String(nextFineType.defaultAmount) : "";

    let nextDueDate = yyyyMmDd();
    const paymentDays = Number(nextFineType?.paymentWindowDays || 0);
    if (paymentDays > 0) {
      const today = new Date();
      today.setDate(today.getDate() + paymentDays);
      nextDueDate = yyyyMmDd(today);
    }

    setDraft((prev) => ({
      ...prev,
      fineTypeId: value,
      amountDue: nextAmount,
      dueDate: nextDueDate,
    }));
  };

  const handleAddBatch = () => {
    if (!canManage) {
      setSnack({ type: "error", message: "You are not authorized to issue group fines." });
      return;
    }

    if (!draft.fineTypeId) {
      setSnack({ type: "error", message: "Select a fine type first." });
      return;
    }

    if (!draft.dueDate) {
      setSnack({ type: "error", message: "Select a due date." });
      return;
    }

    if (draft.amountDue === "" || Number(draft.amountDue) <= 0) {
      setSnack({ type: "error", message: "Amount due must be greater than 0." });
      return;
    }

    if (draft.memberIds.length === 0) {
      setSnack({ type: "error", message: "Select at least one member for this offence." });
      return;
    }

    const fineType = fineTypes.find((ft) => String(ft.id) === String(draft.fineTypeId));
    const item = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      fineTypeId: Number(draft.fineTypeId),
      fineTypeName: fineType?.name || `Fine Type #${draft.fineTypeId}`,
      amountDue: Number(draft.amountDue),
      dueDate: draft.dueDate,
      description: draft.description || null,
      memberIds: [...draft.memberIds],
    };

    setBatchItems((prev) => [...prev, item]);
    setDraft(buildInitialDraft());
  };

  const handleSubmitBulk = async () => {
    if (!canManage) {
      setSnack({ type: "error", message: "You are not authorized to issue group fines." });
      return;
    }

    if (!batchItems.length) {
      setSnack({ type: "error", message: "Add at least one fine batch before issuing." });
      return;
    }

    const memberFines = batchItems.flatMap((batch) =>
      batch.memberIds.map((memberId) => ({
        userId: Number(memberId),
        fineTypeId: Number(batch.fineTypeId),
        amountDue: Number(batch.amountDue),
        dueDate: `${batch.dueDate}T00:00:00Z`,
        statusId: 1,
        description: batch.description || null,
      }))
    );

    try {
      const res = await createMemberFinesBulk({ variables: { memberFines } });

      if (res?.errors?.length) {
        const msg =
          res.errors?.[0]?.extensions?.message ||
          res.errors?.[0]?.message ||
          "Could not issue fines. Please try again.";
        setSnack({ type: "error", message: msg });
        return;
      }

      const createdCount = Number(res?.data?.createMemberFinesBulk ?? memberFines.length);
      setSnack({ type: "success", message: `${createdCount} member fines issued successfully.` });
      setBatchItems([]);
      setDraft(buildInitialDraft());
      setTab(0);
      refetchFines();
    } catch (err) {
      const msg =
        err?.graphQLErrors?.[0]?.extensions?.message ||
        err?.graphQLErrors?.[0]?.message ||
        err?.message ||
        "Could not issue fines. Please try again.";
      setSnack({ type: "error", message: msg });
    }
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.35, minWidth: 80 },
    { field: "memberName", headerName: "Member", flex: 1, minWidth: 180 },
    { field: "fineTypeName", headerName: "Fine Type", flex: 1, minWidth: 160 },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString() : "-"),
    },
    {
      field: "amountDue",
      headerName: "Amount Due",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      flex: 0.8,
      minWidth: 140,
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "outstanding",
      headerName: "Outstanding",
      flex: 0.85,
      minWidth: 150,
      valueGetter: ({ row }) => {
        const due = Number(row.amountDue || 0);
        const paid = Number(row.amountPaid || 0);
        return Math.max(due - paid, 0);
      },
      valueFormatter: ({ value }) => currency(value),
    },
    {
      field: "statusId",
      headerName: "Status",
      flex: 0.7,
      minWidth: 140,
      renderCell: ({ value }) => {
        const s = STATUS[value] || { label: `Unknown (${value})`, color: "default" };
        return <Chip label={s.label} color={s.color} size="small" />;
      },
    },
    { field: "description", headerName: "Description", flex: 1.2, minWidth: 220 },
  ];

  if (finesError) {
    return (
      <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
        <Typography color="error">Error: {finesError.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ m: { xs: "0.75rem", sm: "1rem 1.5rem", md: "1.5rem 2.5rem" }, minWidth: 0 }}>
      <Header title="GROUP FINES" subtitle="Track all group fines and issue fines in bulk after meetings" />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant={isMobile ? "scrollable" : "standard"} scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2 }}>
        <Tab label="Issued Fines" />
        <Tab label="Issue Fines" />
      </Tabs>

      {tab === 0 && (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <TextField
              select
              label="Filter by Status"
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ width: 220 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Due</MenuItem>
              <MenuItem value="2">Partially Paid</MenuItem>
              <MenuItem value="3">Paid</MenuItem>
            </TextField>

            <Button variant="outlined" onClick={() => refetchFines()} disabled={finesLoading}>
              Refresh
            </Button>
          </Stack>

          <Box
            height="75vh"
            sx={{
              "& .MuiDataGrid-root": { border: "none" },
              "& .MuiDataGrid-cell": { borderBottom: "none" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.primary.light,
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
              },
            }}
          >
            <DataGrid loading={finesLoading} rows={filteredRows} getRowId={(row) => row.id} columns={columns} />
          </Box>
        </>
      )}

      {tab === 1 && (
        <Stack spacing={2.5}>
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="h5" mb={1}>
              Add offence batch
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select the offence type, choose affected members, then add as a batch. Repeat for each offence from the
              meeting minutes, then submit once.
            </Typography>

            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label="Fine Type"
                  value={draft.fineTypeId}
                  onChange={(e) => handleFineTypeChange(e.target.value)}
                  fullWidth
                  disabled={!canManage || fineTypesLoading}
                >
                  {fineTypes.map((ft) => (
                    <MenuItem key={ft.id} value={ft.id}>
                      {ft.name}
                      {ft.defaultAmount != null ? ` • ${currency(ft.defaultAmount)}` : ""}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Amount Due (KES)"
                  type="number"
                  value={draft.amountDue}
                  onChange={(e) => setDraft((p) => ({ ...p, amountDue: e.target.value }))}
                  inputProps={{ min: 0, step: "0.01" }}
                  fullWidth
                  disabled={!canManage}
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  type="date"
                  label="Due Date"
                  InputLabelProps={{ shrink: true }}
                  value={draft.dueDate}
                  onChange={(e) => setDraft((p) => ({ ...p, dueDate: e.target.value }))}
                  fullWidth
                  disabled={!canManage}
                />

                <TextField
                  label="Description (optional)"
                  value={draft.description}
                  onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                  fullWidth
                  disabled={!canManage}
                />
              </Stack>

              <Autocomplete
                multiple
                options={members}
                value={selectedMembers}
                loading={usersLoading}
                onChange={(_, values) =>
                  setDraft((prev) => ({ ...prev, memberIds: values.map((v) => Number(v.id)) }))
                }
                getOptionLabel={(option) => option.label}
                filterSelectedOptions
                disabled={!canManage || usersLoading}
                renderInput={(params) => <TextField {...params} label="Affected Members" />}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button variant="contained" onClick={handleAddBatch} disabled={!canManage}>
                  Add Batch
                </Button>
                <Button
                  variant="text"
                  onClick={() => setDraft(buildInitialDraft())}
                  disabled={!canManage}
                >
                  Reset Draft
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.5 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h5">Pending Bulk Issue</Typography>
                <Typography variant="body2" color="text.secondary">
                  {batchItems.length} batches • {preparedFineCount} member fines ready to issue.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubmitBulk}
                disabled={!batchItems.length || creatingBulk || !canManage}
              >
                {creatingBulk ? "Issuing..." : "Issue All Fines"}
              </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {!batchItems.length && (
              <Typography variant="body2" color="text.secondary">
                No offence batches added yet.
              </Typography>
            )}

            <Stack spacing={1.5}>
              {batchItems.map((item, index) => (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={700}>
                        #{index + 1} {item.fineTypeName} • {currency(item.amountDue)}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setBatchItems((prev) => prev.filter((b) => b.id !== item.id))}
                      >
                        Remove
                      </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Due: {new Date(item.dueDate).toLocaleDateString()}
                      {item.description ? ` • ${item.description}` : ""}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {item.memberIds.map((memberId) => (
                        <Chip
                          key={`${item.id}-${memberId}`}
                          size="small"
                          label={membersMap.get(Number(memberId))?.label || `Member #${memberId}`}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      <Snackbar
        open={!!snack.message}
        autoHideDuration={5000}
        onClose={() => setSnack({ type: "success", message: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.type} onClose={() => setSnack({ type: "success", message: "" })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupFines;
